<template>
    <div class="workflow-step-content">
        <BAlert
            v-if="step.error"
            variant="danger"
            show
            class="mb-1"
        >
            <div class="alert-body">
                {{ errorMessage }}
            </div>
        </BAlert>

        <Overlay :show="step.isLoading || translationsLoading">
            <component
                :is="step.component"
                v-if="step.isMounted && translationsReady"
                :context="context"
                :settings="step.settings"
                :runtime-state="step"
                :review-contributions="reviewContributions"
                @validity-change="handleValidityChange"
                @state-change="handleStateChange"
                @save-requested="handleSaveRequested"
                @action="handleAction"
                @execute-terminal="handleTerminalExecution"
                @completed="handleExternalCompletion"
                @effect="handleEffect"
                @error="handleError"
                @open-step="$emit('open-step', $event)"
            />
        </Overlay>

        <div
            v-if="showCompletionAction && step.completion.mode === 'button'"
            class="text-center"
        >
            <WorkflowStepCompletionButton
                class="mt-1"
                :step="step"
                @complete-step="$emit('complete-step', $event)"
            />
        </div>
    </div>
</template>

<script>
import { BAlert } from 'bootstrap-vue'
import Overlay from '@core/components/common/Overlay.vue'
import { loadTranslationGroups } from '@/libs/i18n'
import WorkflowStepCompletionButton from './WorkflowStepCompletionButton.vue'

const normalizeValidation = validation => {
    if (typeof validation === 'boolean') {
        return {
            isValid: validation,
            errors: [],
        }
    }

    return validation
}

export default {
    name: 'WorkflowStepContent',
    components: {
        BAlert,
        Overlay,
        WorkflowStepCompletionButton,
    },
    props: {
        context: {
            type: Object,
            required: true,
        },
        step: {
            type: Object,
            required: true,
        },
        showCompletionAction: {
            type: Boolean,
            default: true,
        },
        reviewContributions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            translationLoadRevision: 0,
            translationsLoading: false,
            translationsReady: this.translationGroupsFor(this.step).length === 0,
        }
    },
    computed: {
        errorMessage() {
            return this.step.error.message || String(this.step.error)
        },
        translationGroups() {
            return this.translationGroupsFor(this.step)
        },
        translationGroupsKey() {
            return this.translationGroups.join('|')
        },
    },
    watch: {
        'step.isMounted': {
            immediate: true,
            handler() {
                this.loadStepTranslations()
            },
        },
        translationGroupsKey() {
            this.loadStepTranslations()
        },
    },
    methods: {
        translationGroupsFor(step) {
            const groups = step && step.descriptor && step.descriptor.translationGroups

            return Array.isArray(groups) ? groups.filter(Boolean) : []
        },
        async loadStepTranslations() {
            const revision = this.translationLoadRevision + 1
            this.translationLoadRevision = revision

            if (!this.step.isMounted || this.translationGroups.length === 0) {
                this.translationsLoading = false
                this.translationsReady = true
                return
            }

            this.translationsLoading = true
            this.translationsReady = false

            try {
                await loadTranslationGroups(this.translationGroups)
            } catch (error) {
                if (revision === this.translationLoadRevision) {
                    this.handleError(error)
                }
            } finally {
                if (revision === this.translationLoadRevision) {
                    this.translationsLoading = false
                    this.translationsReady = true
                }
            }
        },
        handleValidityChange(validation) {
            this.$emit('validity-change', {
                stepId: this.step.id,
                validation: normalizeValidation(validation),
            })
        },
        handleStateChange(partialState) {
            this.$emit('state-change', {
                stepId: this.step.id,
                partialState,
            })
        },
        handleSaveRequested() {
            this.$emit('schedule-step-save', this.step.id)
        },
        handleAction(action) {
            this.$emit('step-action', {
                stepId: this.step.id,
                action,
            })
        },
        handleTerminalExecution() {
            this.$emit('execute-terminal-step', this.step.id)
        },
        handleExternalCompletion(validation) {
            this.$emit('complete-external-step', {
                stepId: this.step.id,
                validation: normalizeValidation(validation),
            })
        },
        handleEffect(effect) {
            this.$emit('effect', {
                ...effect,
                sourceStepId: this.step.id,
            })
        },
        handleError(error) {
            this.$emit('error', {
                stepId: this.step.id,
                error,
            })
        },
    },
}
</script>
